import { Form, Select } from "antd";
import { useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from "recharts";
import { NameType, Payload, ValueType } from "recharts/types/component/DefaultTooltipContent";
import styled from "styled-components";
import _ from "lodash";
import { EstadisticasRequest } from "../EstadisticasApi/useGraficaAPI";
import { years, getMonth, months } from "../Utilidades/Options";
import { Equipo } from "../../equipos/useEquiposAPI";

// Estilos
const Container = styled.div`
position:relative;
`;
const TextTitle = styled.h2`
  margin-top: 0.3rem;
  font-size: 1.1rem;
  font-weight: 550;
  color: var(--primary-color);
  text-align: "center";
  padding-bottom: 1.5rem;
`;
const ChartContainer = styled.div`
position:relative;
width: 100%;
background: white;
padding-top: 2rem;
padding-right:3rem;
padding-bottom: 2rem;
overflow: auto;
`;
const Total = styled.div`
display: flex;
.hidden
{
  color:#9e9e9e!important;
  text-decoration:line-through;
}
& > div
{
  position: sticky;
  left: 0;
  padding-left: 1rem;
  display:flex;
  gap: 1rem;
  span 
  {
    cursor:pointer;
  }
}
margin-left: 2rem;
margin-top: 2rem;
justify-content: space-between;
align-items: center;
`;
const Filters = styled.div`
display: flex;
flex-direction: row;
align-items: center;
margin-top: 1.5rem;
margin-bottom: 0.5rem;
width: 100%;
`;

export type GraficaViewerData = { yName: string, dataArr: { key: number, value: number }[] };

export interface GraficaProps {
  title?: string;
  data: GraficaViewerData[];
  xAxis: (string | number)[];
  yAxis: { name: string | number, color: string }[];
  width?: number;
  height?: number;
  request?: EstadisticasRequest;
  handleChangeGrafico: any;
  equipos: Equipo[] | undefined;
  tooltipLabelFormatter?:(label:any, payload: Payload<ValueType, NameType>[]) => string;  
}

const GraficoEquipo = (props: GraficaProps) => {
  const [ chartType, setChartType ] = useState<('LINE' | 'BAR')>('BAR');
  const [ hiddenY, setHiddenY ] = useState<string[]>([]);
  const toggleY = (yName: string) => {
    setHiddenY(_.includes(hiddenY, yName) ? hiddenY.filter(v => v != yName) : [...hiddenY, ...[yName]]);
  }

  const getFormattedData = () => {
    const array: any = [];    
    for (const xName of props.xAxis) {
      const data: any = { name: xName };      
      for (const i in props.yAxis) {
        const yName = props.yAxis[i].name;        
        data[yName] = props.data.filter(v => v.yName == yName).at(0)?.dataArr.filter(v => v.key == xName).at(0)?.value ?? 0;
      }
      array.push(data);
    }    
    return array;
  };

  const getTotals = () => {
    const totals: { key: string, value: number }[] = [];
    const data = getFormattedData();        
    for (const key in data.at(0)) {
      if (key == 'name') continue;
      totals.push({ key, value: _.chain(data).sumBy(key).value() });
    }    
    return totals;
  };

  return(
    <Container>
      <Form
                initialValues={{
                    ["Year"]: props.request?.CapturasEquipoYear,
                    ["EquipoId"]: props.request?.CapturasEquipoId,
                    ["Month"]: props.request?.CapturasEquipoMonth
                }}
            >
                <Filters>
                    <Form.Item
                        rules={[
                            { required: true, message: "Campo requerido" }
                        ]}
                        required
                        name={"Year"}
                        label="Año"
                        style={{ marginRight: "15px" }}
                    >
                        <Select
                            optionFilterProp="children"
                            allowClear
                            size="middle"
                            style={{ width: "100%" }}                            
                            onChange={(e: number) => props.handleChangeGrafico(2, { name: "year", value: e })}
                        >
                            {
                                years.map(e =>
                                    <Select.Option key={e.id} value={e.value}>{e.value}</Select.Option>
                                )
                            }
                        </Select>
                    </Form.Item>
                    <Form.Item
                        rules={[
                            { required: true, message: "Campo requerido" }
                        ]}
                        required
                        name={"Month"}
                        label="Mes"
                        style={{ marginRight: "15px" }}
                    >
                        <Select
                            optionFilterProp="children"
                            allowClear
                            size="middle"
                            style={{ width: "100%" }}
                            onChange={(e: number) => props.handleChangeGrafico(2, { name: "month", value: e })}
                        >
                            {
                                months.map(e =>
                                    <Select.Option key={e.id} value={e.value}>{getMonth(e.value)}</Select.Option>
                                )
                            }
                        </Select>
                    </Form.Item>
                    <Form.Item
                        rules={[
                            { required: true, message: "Campo requerido" }
                        ]}
                        required
                        name={"EquipoId"}
                        label="Equipo"
                    >
                        <Select
                            optionFilterProp="children"
                            allowClear
                            size="middle"
                            style={{ width: "100%" }}
                            onChange={(e: number) => props.handleChangeGrafico(2, { name: "equipoId", value: e })}
                        >
                            {
                                props.equipos?.map((e: any) =>
                                    <Select.Option key={e.Id} value={e.Id}>{e.Nombre} - {e.NroSerie}</Select.Option>
                                )
                            }
                        </Select>
                    </Form.Item>
                </Filters>
            </Form>
      {props.title && <TextTitle>{props.title}</TextTitle>}      
      <ChartContainer>        
        { // Gráfico de Líneas
          chartType == 'LINE' && 
          <LineChart
          width = { props.width ?? 900 }
          height = { props.height ?? 220 }
          margin = { { top: 0, right: 0, left: 0, bottom: 0 } }
          data = { getFormattedData() }>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis ticks = { props.xAxis } dataKey = {"name"} />
          <YAxis allowDecimals = { false } />
          <Tooltip labelFormatter = { props.tooltipLabelFormatter } />
          <Legend />
          { props.data.map((v, k) => <Line hide = { _.includes(hiddenY, props.yAxis[k]?.name)} key = { k } type = "monotone" dataKey = { props.yAxis[k]?.name } stroke = { props.yAxis[k]?.color } />) }
          </LineChart>
        }

        { // Gráfico de Barras
          chartType == 'BAR' && 
          <BarChart
          width = {props.width ?? 900}
          height = {props.height ?? 220}
          margin = {{ top: 0, right: 0, left: 0, bottom: 0 }}
          data = { getFormattedData() }
        >
          <CartesianGrid strokeDasharray = "3 3" />
          <XAxis ticks = { props.xAxis } dataKey = { "name" } />
          <YAxis allowDecimals = {false} />
          <Tooltip labelFormatter = { props.tooltipLabelFormatter }  />
          <Legend />
          { props.data.map((v, k) => <Bar hide = { _.includes(hiddenY, props.yAxis[k]?.name) } key = { k } type = "monotone" dataKey = { props.yAxis[k]?.name } fill = { props.yAxis[k]?.color } />) }
          </BarChart>
        }

        { // Zona inferior del gráfico
          getTotals().length > 0 && 
          <Total>            
            <div>{getTotals().map((v, k) =>
              <span className = { _.includes(hiddenY, v.key) ? 'hidden' : '' }
                onClick = { () => toggleY(v.key) }
                key = { k }
                style = {{ color: props.yAxis.filter(y => y.name == v.key).at(0)?.color }}><strong>{v.key}:</strong> {v.value}</span>)}
              <strong>Click en el dato para ocultar/mostrar</strong>
            </div>
            <Select value = { chartType } onChange = { (v) => setChartType(v) }>
              <Select.Option value = "LINE">Gráfico de líneas</Select.Option>
              <Select.Option value = "BAR">Gráfico de barras</Select.Option>
            </Select>
          </Total>
        }
      </ChartContainer>
    </Container>
  )
};

export default GraficoEquipo;